import { Object3D, Quaternion, Vector3 } from "three";

import CameraControls from "camera-controls";

import { alphaFilterVector3, disableContextMenu } from "../../lib/misc/util";

import { Actor, findActor } from "../world/actor/actor";
import { Context } from "../context";

import { ControllerStrategy } from "./strategy";
import { SmoothTimes } from "./controller";
import { zoomConfig } from "./zoom";

/**
 * Base class useful for camera modes that track players
 */
abstract class TrackingControllerStrategy extends ControllerStrategy {
  protected posId = -100;

  mound = new Object3D();

  init(context: Context): ControllerStrategy {
    super.init(context);

    this.mound.position.set(0, 0, -65);
    this.mound.name = "cam-tracked-mound";

    if (this.registered?.state.tracked) {
      this.registered.state.tracked = this.mound;
    }

    return this;
  }

  protected findNewActor(
    context: Context,
    newPosId: number
  ): Actor | undefined {
    if (this.posId == newPosId) {
      return undefined;
    }

    this.posId = newPosId;
    return findActor(context, newPosId);
  }
}

/**
 * Stiff follow player and allow user to pan and rotate the camera relative
 * to that player.
 */
export class SpinControllerStrategy extends TrackingControllerStrategy {
  protected offset: Vector3 = new Vector3(0, 1, 0);
  private cPos: Vector3 = new Vector3();
  private pos: Vector3 = new Vector3();

  protected alphaFactor = 0.1 / 60;

  protected cachedActions: {
    right: any;
    touch: {
      two: any;
    };
  } = {
    right: CameraControls.ACTION.NONE,
    touch: {
      two: CameraControls.ACTION.NONE,
    },
  };

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) {
      console.error("no controller registered");
      return this;
    }

    const { controls } = this.registered;

    const actor = this.findNewActor(context, this.registered.state.positionId);
    if (actor) {
      this.registered.state.tracked = actor?.centroid;
    }

    if (controls) {
      controls.getTarget(this.cPos);
      controls.getTarget(this.pos);

      this.cachedActions.right = controls.mouseButtons.right;
      this.cachedActions.touch.two = controls.touches.two;

      controls.mouseButtons.right = CameraControls.ACTION.OFFSET;
      controls.touches.two = CameraControls.ACTION.TOUCH_ZOOM;
    }

    return this;
  }

  update(_deltaS: number, _gameTimeDelta: number, frameNum?: number) {
    if (!this.registered || !this.registered.state.tracked) {
      return;
    }

    if (frameNum != undefined) this.lastFrameNum = frameNum;

    const actor = this.findNewActor(
      this.registered.contextRef,
      this.registered.state.positionId
    );
    if (actor) {
      this.registered.state.tracked = actor?.centroid;
    }

    if (this.registered.state.tracked) {
      this.cPos.addVectors(this.registered.state.tracked.position, this.offset);

      this.pos.copy(this.cPos);

      this.registered.controls?.moveTo(
        this.pos.x,
        this.pos.y,
        this.pos.z,
        true
      );
    } else {
      console.error("no tracked spin");
    }
  }

  teardown() {
    super.teardown();
    const controls = this.registered?.controls;
    if (controls) {
      controls.setFocalOffset(0, 0, 0);

      controls.mouseButtons.right = this.cachedActions.right;
      controls.touches.two = this.cachedActions.touch.two;
    }
  }
}

/**
 * This class doesn't use the controller but rather just moves the camera directly.
 * For this we have to destroy the controller in init and recreate it in teardown. *
 */
export class ThirdPersonControllerStrategy extends TrackingControllerStrategy {
  protected offset: Vector3 = new Vector3(20, 3, 0);
  private cTarget: Vector3 = new Vector3();
  private cPos: Vector3 = new Vector3();
  private target: Vector3 = new Vector3();
  private pos: Vector3 = new Vector3();

  private tQ?: Quaternion;

  protected alphaFactor = 0.025 / 60;

  boneName = "joint_TorsoB";

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) {
      console.error("no controller registered");
      return this;
    }

    if (this.registered.controls) {
      this.registered.controls.getPosition(this.pos);
      this.registered.controls.getPosition(this.cPos);
      this.registered.controls.getTarget(this.cTarget);
      this.registered.controls.getTarget(this.target);
      this.registered.controls.dispose();
      this.registered.controls = undefined;
    }

    const dom = this.registered.domRef;
    dom.addEventListener("contextmenu", disableContextMenu);

    const actor = this.findNewActor(context, this.registered.state.positionId);
    if (actor) {
      this.registered.state.tracked = this.findBone(actor);
    }

    return this;
  }

  update(deltaS: number, _gameTimeDelta: number, frameNum?: number) {
    if (!this.registered || !this.registered.state.tracked) {
      return;
    }

    if (frameNum != undefined) this.lastFrameNum = frameNum;

    const actor = this.findNewActor(
      this.registered.contextRef,
      this.registered.state.positionId
    );
    if (actor) {
      this.registered.state.tracked = this.findBone(actor);
    }

    if (!this.registered.state.tracked) {
      return;
    }

    const camera = this.registered.camera;

    this.cTarget.copy(this.registered.state.tracked.position);

    deltaS = 1 / 60;
    const alpha = this.alphaFactor / deltaS;

    alphaFilterVector3(this.target, this.cTarget, alpha);

    this.cPos.copy(this.offset);

    if (!this.tQ) {
      this.tQ = new Quaternion();
      this.tQ.copy(this.registered.state.tracked.quaternion);
    }

    this.tQ.slerp(this.registered.state.tracked.quaternion, 0.1);

    this.cPos.applyQuaternion(this.tQ);

    this.cPos.add(this.target);

    alphaFilterVector3(this.pos, this.cPos, alpha);

    camera.position.copy(this.pos);
    camera.lookAt(this.target);
    camera.updateProjectionMatrix();
  }

  teardown() {
    if (this.registered) {
      this.registered.controls = new CameraControls(
        this.registered.camera,
        this.registered.domRef
      );

      this.registered.controls.setPosition(this.pos.x, this.pos.y, this.pos.z);
      this.registered.controls.setTarget(
        this.target.x,
        this.target.y,
        this.target.z
      );
    }

    const dom = this.registered?.domRef;
    dom?.removeEventListener("contextmenu", disableContextMenu);
  }

  protected findBone(actor?: Actor): Object3D | undefined {
    if (!actor) return undefined;

    const target = actor.bones.getObjectByName(this.boneName);
    if (target) {
      return target;
    } else {
      console.error(
        `target ${this.boneName} not found to follow for pos ${this.posId}`
      );
      return undefined;
    }
  }
}

/**
 * Tracks ball but allows for player control - similar to spin camera
 */
export class BallTrackerControllerStrategy extends SpinControllerStrategy {
  offset: Vector3 = new Vector3(0, 1, 0);

  alphaFactor = 0.025 / 60;

  init(context: Context): ControllerStrategy {
    if (!this.registered) {
      console.error("no controller registered");
      return this;
    }
    super.init(context);

    this.registered.state.tracked = context.world?.ball.object;

    return this;
  }

  protected findNewActor(
    _context: Context,
    _newPosId: number
  ): Actor | undefined {
    return undefined;
  }
}

export class LookAtPlayerControllerStrategy extends TrackingControllerStrategy {
  private target: Vector3 = new Vector3();
  private pos: Vector3 = new Vector3();
  private temp: Vector3 = new Vector3();
  private dir: Vector3 = new Vector3();

  private followOffCb = () => {
    this.targetDist = this.pos.distanceTo(this.target);
    if (this.registered) this.registered.state.following = false;
  };
  private followOnCb = () => {
    this.registered?.controls?.getTarget(this.target);
    this.registered?.controls?.getPosition(this.pos);

    this.target.sub(this.pos).setLength(this.targetDist).add(this.pos);

    if (this.registered) this.registered.state.following = true;
  };

  private camDist = 0.00001;
  private targetDist = 10;

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) {
      console.error("no controller registered");
      return this;
    }

    this.registered.state.following = true;

    const { controls, domRef } = this.registered;

    const actor = this.findNewActor(context, this.registered.state.positionId);
    if (actor) {
      this.registered.state.tracked = actor.centroid;
    }

    if (controls) {
      controls.getPosition(this.pos);
      controls.getTarget(this.target);

      this.dir.copy(this.target).sub(this.pos).setLength(this.camDist);

      controls.mouseButtons.right = CameraControls.ACTION.NONE;

      // TODO: improve this - assumes we're combining this with the zoomFOV strategy
      controls.touches.two = CameraControls.ACTION.TOUCH_ZOOM;
    } else {
      console.error("no controls to take over");
    }

    domRef?.addEventListener("pointerdown", this.followOffCb);
    domRef?.addEventListener("pointerup", this.followOnCb);
    domRef?.addEventListener("pointerleave", this.followOnCb);

    return this;
  }

  update(_deltaS: number, _gameTimeDelta: number, frameNum?: number) {
    const context = this.registered?.contextRef;
    const controls = this.registered?.controls;
    const tracked = this.registered?.state.tracked;

    if (!this.registered || !tracked || !context || !controls) {
      return;
    }

    if (this.registered.state.following) {
      const actor = this.findNewActor(
        context,
        this.registered.state.positionId
      );
      if (actor) {
        this.registered.state.tracked = actor.centroid;
      }

      this.temp.copy(tracked.position);

      // TODO: make this frame rate invariant
      this.target.lerp(this.temp, 0.1);

      controls.getPosition(this.pos);

      this.dir
        .subVectors(this.target, this.pos)
        .setLength(this.camDist)
        .add(this.pos);
      controls.setTarget(this.dir.x, this.dir.y, this.dir.z, false);
    }

    if (frameNum != undefined) this.lastFrameNum = frameNum;
  }

  teardown() {
    super.teardown();

    if (!this.registered) return;

    const { controls, domRef } = this.registered;
    if (controls) {
      controls.setPosition(this.pos.x, this.pos.y, this.pos.z, true);
      controls.setTarget(this.target.x, this.target.y, this.target.z, true);

      controls.mouseButtons.right = CameraControls.ACTION.TRUCK;
    }

    if (this.followOffCb && this.followOnCb) {
      domRef?.removeEventListener("pointerdown", this.followOffCb);
      domRef?.removeEventListener("pointerup", this.followOnCb);
      domRef?.removeEventListener("pointerleave", this.followOnCb);
    }
  }
}

export class LookAtBallControllerStrategy extends LookAtPlayerControllerStrategy {
  offset: Vector3 = new Vector3(0, 1, 0);
  pointedAtBall = false;

  maxAge = 100;

  init(context: Context): ControllerStrategy {
    if (!this.registered) {
      console.error("no controller registered");
      return this;
    }

    super.init(context);

    const ball = context.world?.ball;

    this.registered.state.tracked =
      ball?.age != undefined && ball.age < this.maxAge
        ? ball.object
        : this.mound;

    return this;
  }

  update(deltaS: number, gameTimeDelta: number, frameNum?: number) {
    const toUpdate = frameNum == undefined || this.lastFrameNum != frameNum;

    super.update(deltaS, gameTimeDelta, frameNum);

    if (!toUpdate || !this.registered) return;

    const ball = this.registered.contextRef.world?.ball;
    const context = this.registered.contextRef;

    if (ball && ball.age != undefined && ball.age < this.maxAge) {
      if (!this.pointedAtBall) {
        this.registered.state.tracked = ball.object;
        this.pointedAtBall = true;
      }
    } else if (this.pointedAtBall) {
      const actor = super.findNewActor(
        context,
        this.registered.state.positionId
      );
      if (!actor) {
        console.error("pointing at mound!");
      }
      this.registered.state.tracked = actor ? actor.centroid : this.mound;
      this.pointedAtBall = false;
    }

    if (frameNum != undefined) this.lastFrameNum = frameNum;
  }

  protected findNewActor(
    _context: Context,
    _newPosId: number
  ): Actor | undefined {
    return undefined;
  }
}

export class PairControllerStrategy extends TrackingControllerStrategy {
  private target: Vector3 = new Vector3();
  private pos: Vector3 = new Vector3();
  private dir: Vector3 = new Vector3();

  private posOffset = new Vector3(0, 5, 0);
  private targetOffset = new Vector3(0, 1, 0);

  private maxTargetHeight = 10;
  private minPositionHeight = 3;

  private camDist = 30;
  private maxInclination = (15 * Math.PI) / 180;

  private zooming = false;
  private zoomTimeoutMs = 3000;
  private zoomSpeedFactor = 0.33;
  private wheelScrollCnt = 0;
  private manualZoomDistance = 0;

  private cachedActions: {
    right: any;
    wheel: any;
    touch: {
      two: any;
      three: any;
    };
  } = {
    right: CameraControls.ACTION.NONE,
    wheel: CameraControls.ACTION.NONE,
    touch: {
      two: CameraControls.ACTION.NONE,
      three: CameraControls.ACTION.NONE,
    },
  };

  private cachedSmoothTime = SmoothTimes.DEFAULT;

  private pointerDownCb = (ev: PointerEvent) => {
    // ignore right clicks
    if (ev.button == 2) {
      return;
    }

    // ev.pointerType
    this.registered?.addAuxStrategy(SpinControllerStrategy);
    if (this.registered) {
      this.registered.state.following = false;

      if (this.registered.controls) {
        this.cachedSmoothTime = this.registered.controls.smoothTime;
        this.registered.controls.smoothTime = SmoothTimes.MEDIUM;
      }
    }
  };
  private pointerUpCb = (ev?: PointerEvent) => {
    if (ev && ev.button == 2) {
      return;
    }
    this.registered?.removeAuxStrategy(SpinControllerStrategy);
    if (this.registered) {
      this.registered.state.following = true;

      // TODO: improve timeout with update to newer camera controls
      setTimeout(() => {
        const controls = this.registered?.controls;
        if (controls) {
          controls.smoothTime = this.cachedSmoothTime;
          // TODO: support return to zoom home on mobile
          // controls.zoomTo(1, true);
        }
      }, 2000);
    }
  };

  private manualZoomCb = (deltaY: number) => {
    this.zooming = true;
    this.wheelScrollCnt++;

    this.manualZoomDistance -=
      deltaY * zoomConfig.dolly.speed * this.zoomSpeedFactor;

    setTimeout(() => {
      this.wheelScrollCnt = Math.max(this.wheelScrollCnt - 1, 0);
      if (this.wheelScrollCnt == 0) {
        this.zooming = false;
        this.manualZoomDistance = 0;
      }
    }, this.zoomTimeoutMs);
  };
  private mouseZoomCb = (e: WheelEvent) => this.manualZoomCb(e.deltaY);

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) {
      console.error("no controller registered");
      return this;
    }

    this.registered.state.following = true;

    const { controls, domRef } = this.registered;

    this.updateTracked();

    if (controls) {
      controls.getPosition(this.pos);
      controls.getTarget(this.target);

      this.dir.subVectors(this.target, this.pos).setLength(this.camDist);

      this.cachedActions.right = controls.mouseButtons.right;
      this.cachedActions.wheel = controls.mouseButtons.wheel;
      this.cachedActions.touch.two = controls.touches.two;
      this.cachedActions.touch.three = controls.touches.three;

      controls.mouseButtons.right = CameraControls.ACTION.NONE;
      controls.mouseButtons.wheel = CameraControls.ACTION.NONE;

      controls.touches.two = CameraControls.ACTION.NONE;
      controls.touches.three = CameraControls.ACTION.NONE;
    } else {
      console.error("no controls to take over");
    }

    domRef.addEventListener("wheel", this.mouseZoomCb);
    domRef.addEventListener("pointerdown", this.pointerDownCb);
    domRef.addEventListener("pointerup", this.pointerUpCb);
    domRef.addEventListener("pointerleave", this.pointerUpCb);

    return this;
  }

  update(_deltaS: number, _gameTimeDelta: number, frameNum?: number) {
    const controls = this.registered?.controls;

    if (!this.registered || !controls) {
      return;
    }

    if (this.registered.state.following) {
      this.updateTracked();

      const tracked = this.registered?.state.tracked;
      if (!tracked) {
        return;
      }
      const other = this.registered?.state.trackedOther;

      const softTransition = !this.registered.state.teleport;
      if (!softTransition) {
        this.registered.state.teleport = false;
      }

      this.target.addVectors(tracked.position, this.targetOffset);
      let positioned = false;

      let maxZoomDist = 0;
      let minZoomDist = -zoomConfig.dolly.max;

      if (other) {
        let h = new Vector3()
          .subVectors(other.position, tracked.position)
          .length();
        let boundedOther = other.position
          .clone()
          .setY(Math.min(h * Math.sin(this.maxInclination), other.position.y));

        this.dir
          .subVectors(boundedOther, tracked.position)
          .setLength(this.camDist);

        this.pos.subVectors(this.target, this.dir).add(this.posOffset);

        this.target.set(
          this.target.x,
          Math.min(this.target.y, this.maxTargetHeight),
          this.target.z
        );
        this.pos.set(
          this.pos.x,
          Math.max(this.pos.y, this.minPositionHeight),
          this.pos.z
        );

        maxZoomDist = this.pos.distanceTo(this.target) - zoomConfig.dolly.min;

        positioned = true;
      } else if (this.registered.state.homePos) {
        this.target.set(
          this.target.x,
          Math.min(this.target.y, this.maxTargetHeight),
          this.target.z
        );
        this.pos.set(
          this.registered.state.homePos.x,
          this.registered.state.homePos.y,
          this.registered.state.homePos.z
        );

        // TODO: replace this magic number with something more robust
        //       (currently makes it so the user doesn't zoom past pitcher
        //        in personal / pitcher cam)
        maxZoomDist = 20;

        positioned = true;
      }

      if (positioned) {
        if (this.zooming) {
          // do zoom explicitly - since we own the position of the camera
          // in this strategy
          this.dir.subVectors(this.target, this.pos).normalize();

          if (this.manualZoomDistance > maxZoomDist) {
            this.manualZoomDistance = maxZoomDist;
          } else if (this.manualZoomDistance < minZoomDist) {
            this.manualZoomDistance = minZoomDist;
          }

          this.dir.multiplyScalar(this.manualZoomDistance);

          this.pos.add(this.dir);
        }

        controls.setTarget(
          this.target.x,
          this.target.y,
          this.target.z,
          softTransition
        );
        controls.setPosition(
          this.pos.x,
          this.pos.y,
          this.pos.z,
          softTransition
        );
      } else {
        controls.moveTo(
          this.target.x,
          Math.min(this.target.y, this.maxTargetHeight),
          this.target.z,
          softTransition
        );
      }
    }

    if (frameNum != undefined) this.lastFrameNum = frameNum;
  }

  protected updateTracked() {
    const context = this.registered?.contextRef;
    const state = this.registered?.state;
    // NB: if the controller is set to not autoTrack, then we rely on tracked
    //     and trackedOther to be set elsewhere
    if (!context || !state || !state.autoTrack) {
      return;
    }
    const foreGround = findActor(context, state.positionId);
    if (foreGround) {
      state.tracked = foreGround.centroid;
    }

    const backGround = findActor(context, state.positionIdOther);
    if (backGround) {
      state.trackedOther = backGround.centroid;
    }
  }

  teardown() {
    super.teardown();

    if (!this.registered) return;

    const { controls, domRef, state } = this.registered;
    if (controls) {
      controls.setPosition(this.pos.x, this.pos.y, this.pos.z, true);
      controls.setTarget(this.target.x, this.target.y, this.target.z, true);

      controls.mouseButtons.right = this.cachedActions.right;
      controls.mouseButtons.wheel = this.cachedActions.wheel;

      controls.touches.two = this.cachedActions.touch.two;
      controls.touches.three = this.cachedActions.touch.three;
    }

    if (!state.following) {
      this.pointerUpCb();
    }

    domRef.removeEventListener("wheel", this.mouseZoomCb);
    domRef.removeEventListener("pointerdown", this.pointerDownCb);
    domRef.removeEventListener("pointerup", this.pointerUpCb);
    domRef.removeEventListener("pointerleave", this.pointerUpCb);
  }
}

export class LookAtFriendlyTransitionControllerStrategy extends ControllerStrategy {
  private pos: Vector3 = new Vector3();
  private target: Vector3 = new Vector3();
  private delta: Vector3 = new Vector3();

  private startPos: Vector3 = new Vector3();
  private endPos?: Vector3;
  private transElapsedS = 0;
  private transDurationS = 2;

  protected deltaT = {
    slow: 8,
    fast: 2,
  };

  update(deltaS: number, _gameTimeDelta: number, _frameNum?: number) {
    if (!this.registered) {
      console.error("no controls registered");
      return;
    }

    const controls = this.registered.controls;

    if (this.endPos) {
      controls?.getPosition(this.pos);
      controls?.getTarget(this.target);

      this.delta.copy(this.pos);

      let t = this.transElapsedS / this.transDurationS;
      t = t * t * (3 - 2 * t);

      if (this.transElapsedS < this.transDurationS) {
        this.pos.lerpVectors(this.startPos, this.endPos, t);
      } else {
        this.pos.copy(this.endPos);

        this.endPos = undefined;
      }

      this.delta.subVectors(this.pos, this.delta);
      this.target.add(this.delta);

      controls?.setPosition(this.pos.x, this.pos.y, this.pos.z, false);
      controls?.setTarget(this.target.x, this.target.y, this.target.z, true);

      this.transElapsedS += deltaS;
    } else {
      this.transElapsedS = 0;
    }
  }

  protected setupTransition(endPos: Vector3, duration?: number) {
    if (!this.registered) return;

    const { controls, contextRef } = this.registered;
    if (!controls) return;

    if (this.endPos && endPos.distanceTo(this.endPos) < 0.1) {
      return;
    }

    if (!duration) {
      duration = contextRef.state.actionCamTransDuration;
    }

    this.transDurationS = duration;

    controls.getPosition(this.startPos);
    this.endPos = endPos;
  }

  protected cancelTransition() {
    this.endPos = undefined;
  }
}
